import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import apiOffers from 'api/offers';
import IOfferChoice from 'api/interfaces/offers/IOfferChoice';

// The dropdown lists of the offers the admins manage (backend OfferChoiceLists)
export const BUILDING_TYPE = 'building-type';
export const COLLATERAL = 'collateral';
export const CONTACT_ROLE = 'contact-role';
export const CONTRACT_TYPE = 'contract-type';
export const EMPLOYEE_INFORMATION = 'employee-information';
export const PAYMENT_TERM = 'payment-term';
export const WAY_OF_RECEIVING = 'way-of-receiving';
export const WORK_TYPE = 'work-type';
// The types of the contacts (companies), e.g. customer
export const CONTACT_TYPE = 'contact-type';
// Suggestions for the title of a contact person, which stays free text
export const CONTACT_TITLE = 'contact-title';
// The categories of the rows of the calculation window
export const MATERIAL_CATEGORY = 'material-category';
export const INSTALLATION_CATEGORY = 'installation-category';
export const OTHER_EXPENSE_CATEGORY = 'other-expense-category';

export const OFFER_CHOICE_LISTS = [
  CONTRACT_TYPE,
  WORK_TYPE,
  BUILDING_TYPE,
  EMPLOYEE_INFORMATION,
  WAY_OF_RECEIVING,
  COLLATERAL,
  PAYMENT_TERM,
  CONTACT_ROLE,
  CONTACT_TYPE,
  CONTACT_TITLE,
  MATERIAL_CATEGORY,
  INSTALLATION_CATEGORY,
  OTHER_EXPENSE_CATEGORY,
];

// All the choices with the language of the user, for the category dropdowns
export interface ICategoryChoices {
  choices: IOfferChoice[];
  language: string;
}

// Loaded once for all the lists of the page; loaded again after an admin has saved
let loading: Promise<IOfferChoice[]> | null = null;
const listeners = new Set<(choices: IOfferChoice[]) => void>();

const load = (): Promise<IOfferChoice[]> => {
  if (loading === null) {
    loading = apiOffers.choices.get().catch(() => {
      loading = null;
      return [];
    });
  }
  return loading;
};

export const reloadOfferChoices = async (): Promise<IOfferChoice[]> => {
  loading = null;
  const choices = await load();
  listeners.forEach((listener) => listener(choices));
  return choices;
};

// All the choices, the hidden ones too; empty until loaded
export const useOfferChoices = (): IOfferChoice[] => {
  const [choices, setChoices] = useState<IOfferChoice[]>([]);

  useEffect(() => {
    let active = true;
    const listener = (loaded: IOfferChoice[]) => {
      if (active) {
        setChoices(loaded);
      }
    };
    listeners.add(listener);
    load().then(listener);

    return () => {
      active = false;
      listeners.delete(listener);
    };
  }, []);

  return choices;
};

// The name in the language of the user, in Finnish when not given in it
export const getChoiceLabel = (
  choice: IOfferChoice,
  language: string
): string => {
  let label = choice.labelFi;
  if (language.startsWith('sv')) {
    label = choice.labelSv;
  } else if (language.startsWith('en')) {
    label = choice.labelEn;
  }

  return label || choice.labelFi || choice.value;
};

/**
 * The choices of a list in their order: without the hidden ones, except the current value
 * (e.g. of an offer saved before the choice was hidden), and an unknown current value as it
 * is, so nothing saved disappears.
 */
export const getChoiceList = (
  choices: IOfferChoice[],
  list: string,
  // The current value, or the current values of a choice of many (e.g. the types of a contact)
  current: string | null | undefined | string[],
  language: string
): { label: string; value: string }[] => {
  const kept = (Array.isArray(current) ? current : [current]).filter(
    (value): value is string => Boolean(value)
  );
  const options = choices
    .filter(
      (choice) =>
        choice.list === list && (!choice.hidden || kept.includes(choice.value))
    )
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((choice) => ({
      label: getChoiceLabel(choice, language),
      value: choice.value,
    }));

  kept
    .filter((value) => !options.some((option) => option.value === value))
    .forEach((value) => options.push({ label: value, value }));

  return options;
};

export const useChoiceList = (
  list: string,
  current?: string | null
): { label: string; value: string }[] => {
  const choices = useOfferChoices();
  const { i18n } = useTranslation();

  return getChoiceList(choices, list, current, i18n.language);
};

export const useCategoryChoices = (): ICategoryChoices => {
  const choices = useOfferChoices();
  const { i18n } = useTranslation();

  return { choices, language: i18n.language };
};

// The choices as the options of a select, an empty choice first
export const toOptions = (
  options: { label: string; value: string }[],
  withEmpty = true
): JSX.Element[] => [
  ...(withEmpty
    ? [
        <option key="" value="">
          -
        </option>,
      ]
    : []),
  ...options.map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  )),
];
